import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, HandCoins, Loader2, MapPinned, Newspaper, Users } from "lucide-react";
import api, { API } from "@/lib/api";
import PublicBeneficiaryInfographic from "@/components/PublicBeneficiaryInfographic";

const RUPIAH_FORMATTER = new Intl.NumberFormat("id-ID", {
  style: "currency",
  currency: "IDR",
  maximumFractionDigits: 0,
});

const numberFormatter = new Intl.NumberFormat("id-ID");

function slugify(value = "") {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

function bannerUrl(path) {
  return path ? `${API.replace("/api", "")}${path}` : "";
}

function PublicStatCard({ eyebrow, label, value, icon: Icon, tone, testId }) {
  return (
    <article
      className="overflow-hidden rounded-lg border border-gray-200 bg-white"
      data-testid={testId}
    >
      <div className="flex items-center justify-between border-b border-gray-100 bg-[#F9FAFB] px-5 py-3">
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#0B6B3A]">
          {eyebrow}
        </p>
        <span
          className={`flex h-8 w-8 items-center justify-center rounded-md ${tone.iconClass}`}
          aria-hidden="true"
        >
          <Icon className="h-4 w-4" />
        </span>
      </div>
      <div className="flex items-end justify-between gap-4 px-5 py-5">
        <div>
          <p className="text-sm font-semibold text-[#6B7280]">{label}</p>
          <p className="mt-1 font-display text-3xl font-extrabold text-[#0B6B3A]">{value}</p>
        </div>
        <span
          className="h-11 w-1 shrink-0 rounded-full"
          style={{ backgroundColor: tone.accent }}
          aria-hidden="true"
        />
      </div>
    </article>
  );
}

function NewsCard({ news, index }) {
  const imageUrl = bannerUrl(news.banner_url);

  return (
    <article
      className="overflow-hidden rounded-lg border border-gray-200 bg-white transition-transform duration-200 hover:-translate-y-1"
      data-testid={`public-dashboard-news-card-${index}`}
    >
      <div className="relative aspect-[1.55] bg-[#E8F6EE]">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={news.title}
            className="h-full w-full object-cover"
            data-testid={`public-dashboard-news-image-${index}`}
          />
        ) : (
          <div className="flex h-full items-center justify-center px-5 text-center text-xs font-extrabold text-[#0B6B3A]">
            {news.category || "MDJ Scholarship"}
          </div>
        )}
        {news.date && (
          <span className="absolute left-3 top-3 rounded bg-white/95 px-2 py-1 text-[10px] font-extrabold text-[#0B6B3A]">
            {news.date}
          </span>
        )}
      </div>
      <div className="p-5">
        <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#0B6B3A]">
          {news.category || "Informasi"}
        </p>
        <h3 className="mt-2 line-clamp-2 font-display text-lg font-extrabold text-[#1F2937]">
          {news.title}
        </h3>
        <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-[#6B7280]">
          {news.summary}
        </p>
        <Link
          to={`/berita/${slugify(news.title)}`}
          data-testid={`public-dashboard-news-link-${index}`}
          className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-[#0B6B3A] transition-colors hover:text-[#27AE60]"
        >
          Baca Selengkapnya
          <span aria-hidden="true">→</span>
        </Link>
      </div>
    </article>
  );
}

export default function PublicDashboard() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [logoUrl, setLogoUrl] = useState("");

  useEffect(() => {
    api.get("/public/dashboard-summary")
      .then((response) => setData(response.data))
      .catch(() => setError(true));
    api.get("/site/content")
      .then((response) => {
        if (response.data.logo_url) {
          setLogoUrl(`${API}/site/logo`);
        }
      })
      .catch(() => setLogoUrl(""));
  }, []);

  if (!data && !error) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-[#F6FAF7]">
        <Loader2 className="h-7 w-7 animate-spin text-[#27AE60]" data-testid="public-dashboard-loading" />
      </main>
    );
  }

  if (error) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-[#F6FAF7] p-6">
        <div className="max-w-md rounded-lg border border-[#FECACA] bg-white p-6 text-center">
          <p className="text-sm font-bold text-[#991B1B]" data-testid="public-dashboard-error">
            Data dashboard publik belum dapat dimuat.
          </p>
          <Link to="/" className="mt-4 inline-block text-sm font-bold text-[#0B6B3A]">
            Kembali ke Beranda
          </Link>
        </div>
      </main>
    );
  }

  const regions = data.regions || [];
  const totalBeneficiaries = Number(data.total_beneficiaries || 0);
  const totalDisbursed = Number(data.total_disbursed_amount || 0);
  const regionsWithBeneficiaries = regions.filter((region) => region.count > 0).length;

  return (
    <main className="min-h-screen bg-[#F6FAF7] text-[#1F2937]" data-testid="public-dashboard-page">
      <header className="border-b border-gray-200 bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-4 sm:px-6 lg:px-8">
          <Link to="/" className="flex min-w-0 items-center gap-3" data-testid="public-dashboard-brand-link">
            <span
              className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-[#27AE60] font-display text-xs font-extrabold text-white shadow-md shadow-[#27AE60]/30"
              data-testid="public-dashboard-brand-mark"
            >
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt="Logo MDJ Scholarship"
                  className="h-full w-full object-contain"
                  data-testid="public-dashboard-brand-logo-image"
                />
              ) : (
                "MDJ"
              )}
            </span>
            <span className="min-w-0">
              <strong className="block truncate font-display text-base font-extrabold text-[#1F2937] leading-tight">
                MDJ Scholarship
              </strong>
              <span className="block truncate text-[10px] font-semibold text-[#6B7280] leading-tight">
                BAZNAS (BAZIS) DKI Jakarta
              </span>
            </span>
          </Link>
          <Link
            to="/"
            data-testid="public-dashboard-home-link"
            className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm font-bold text-[#0B6B3A] transition-colors hover:border-[#8ED0A8] hover:bg-[#F0FBF5]"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="hidden sm:inline">Kembali ke Beranda</span>
          </Link>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8 lg:py-12">
        <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="public-dashboard-stats">
          <PublicStatCard
            eyebrow="Penerima Manfaat"
            label="Total Penerima Manfaat"
            value={numberFormatter.format(totalBeneficiaries)}
            icon={Users}
            tone={{ accent: "#27AE60", iconClass: "bg-[#E8F6EE] text-[#0B6B3A]" }}
            testId="public-dashboard-beneficiary-total"
          />
          <PublicStatCard
            eyebrow="Cakupan Wilayah"
            label="Wilayah dengan Penerima"
            value={numberFormatter.format(regionsWithBeneficiaries)}
            icon={MapPinned}
            tone={{ accent: "#F2C94C", iconClass: "bg-[#FFF8D8] text-[#8A6900]" }}
            testId="public-dashboard-region-total"
          />
          <PublicStatCard
            eyebrow="Realisasi Pencairan"
            label="Total Dana Sudah Dicairkan"
            value={RUPIAH_FORMATTER.format(totalDisbursed)}
            icon={HandCoins}
            tone={{ accent: "#0B6B3A", iconClass: "bg-[#E8F6EE] text-[#0B6B3A]" }}
            testId="public-dashboard-disbursed-total"
          />
        </section>

        <section className="mt-6" data-testid="public-dashboard-chart-section">
          <PublicBeneficiaryInfographic
            regions={regions}
            totalBeneficiaries={totalBeneficiaries}
          />
        </section>

        <section className="mt-12" data-testid="public-dashboard-news-section">
          <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0B6B3A]">
                Informasi Terkini
              </p>
              <h2 className="mt-2 font-display text-3xl font-extrabold text-[#1F2937]">
                Berita Terbaru
              </h2>
            </div>
            <Link
              to="/berita"
              data-testid="public-dashboard-all-news-link"
              className="text-sm font-bold text-[#0B6B3A] transition-colors hover:text-[#27AE60]"
            >
              Lihat Semua Berita →
            </Link>
          </div>

          {data.latest_news?.length ? (
            <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
              {data.latest_news.map((news, index) => (
                <NewsCard key={`${news.title}-${index}`} news={news} index={index} />
              ))}
            </div>
          ) : (
            <div className="border border-dashed border-gray-300 bg-white px-5 py-10 text-center">
              <Newspaper className="mx-auto h-7 w-7 text-[#27AE60]" aria-hidden="true" />
              <p className="mt-3 text-sm font-semibold text-[#6B7280]" data-testid="public-dashboard-news-empty">
                Berita terbaru akan tampil di sini.
              </p>
            </div>
          )}
        </section>

        <footer className="mt-12 border-t border-gray-200 py-6 text-xs text-[#6B7280]">
          <div className="flex flex-col justify-between gap-2 sm:flex-row sm:items-center">
            <p data-testid="public-dashboard-copyright">
              © {new Date().getFullYear()} BAZNAS (BAZIS) Provinsi DKI Jakarta
            </p>
            <p data-testid="public-dashboard-updated-at">
              Pembaruan data: {data.updated_at || "tersedia secara berkala"}
            </p>
          </div>
        </footer>
      </div>
    </main>
  );
}